import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class VisualNotificationService {
  async showCustom(options: {
    title: string;
    message: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    position?: 'center' | 'top' | 'bottom';
    confirmText?: string;
    showCloseButton?: boolean;
    customIcon?: string;
    customColor?: string;
    backdrop?: boolean;
  }) {
    return Swal.fire({
      title: options.title,
      html: options.message,
      icon: options.customIcon ? undefined : 'info',
      showCloseButton: options.showCloseButton ?? true,
      confirmButtonText: options.confirmText || 'Aceptar',
      customClass: {
        popup: options.size === 'lg' ? 'swal2-large' : options.size === 'xl' ? 'swal2-xlarge' : '',
      },
      position: options.position || 'center',
      backdrop: options.backdrop !== false,
      iconColor: options.customColor,
      didOpen: (el) => {
        if (options.customIcon) {
          const icon = document.createElement('i');
          icon.className = options.customIcon + ' fa-2x';
          icon.style.color = options.customColor || '#2563eb';
          el.querySelector('.swal2-title')?.prepend(icon);
        }
      },
    });
  }

  async showSuccess(title: string, message: string) {
    return Swal.fire({
      icon: 'success',
      title,
      html: message,
      confirmButtonText: 'Aceptar',
      timer: 3000
    });
  }

  async showError(title: string, message: string) {
    return Swal.fire({
      icon: 'error',
      title,
      html: message,
      confirmButtonText: 'Aceptar',
      timer: 4000
    });
  }

  async showWarning(title: string, message: string) {
    return Swal.fire({
      icon: 'warning',
      title,
      html: message,
      confirmButtonText: 'Aceptar',
      timer: 3500
    });
  }

  async showInfo(title: string, message: string) {
    return Swal.fire({
      icon: 'info',
      title,
      html: message,
      confirmButtonText: 'Aceptar',
      timer: 3000
    });
  }

  // ===================================
  // MÉTODOS PARA MÓDULO DE CAJA
  // ===================================

  /**
   * Alert personalizado para el módulo de caja con título "CreediTotal"
   * Mantiene el comportamiento exacto de alert() nativo
   */
  async crediAlert(message: string, icon: SweetAlertIcon = 'info') {
    return Swal.fire({
      title: 'CreediTotal',
      html: message,
      icon: icon,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3B82F6',
      backdrop: true,
      allowOutsideClick: false,  // Como las alertas nativas, no se puede cerrar haciendo click afuera
      allowEscapeKey: false,     // Como las alertas nativas, no se puede cerrar con ESC
      timer: undefined,          // Sin timer automático, como las nativas
      timerProgressBar: false,   // Sin barra de progreso
      showCloseButton: false,    // Sin botón X, como las nativas
      customClass: {
        popup: 'credi-alert-popup',
        title: 'credi-alert-title',
        htmlContainer: 'credi-alert-content'
      }
    });
  }

  /**
   * Confirmación personalizada para el módulo de caja con título "CreediTotal"
   * Mantiene el comportamiento exacto de confirm() nativo - retorna boolean
   */
  async crediConfirm(message: string, confirmText: string = 'Aceptar', cancelText: string = 'Cancelar'): Promise<boolean> {
    const result = await Swal.fire({
      title: 'CreediTotal',
      html: message,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      confirmButtonColor: '#3B82F6',
      cancelButtonColor: '#6B7280',
      backdrop: true,
      allowOutsideClick: false,  // Como confirm() nativo, no se puede cerrar haciendo click afuera
      allowEscapeKey: false,     // Como confirm() nativo, no se puede cerrar con ESC
      timer: undefined,          // Sin timer automático, como las nativas
      timerProgressBar: false,   // Sin barra de progreso
      showCloseButton: false,    // Sin botón X, como las nativas
      customClass: {
        popup: 'credi-confirm-popup',
        title: 'credi-confirm-title',
        htmlContainer: 'credi-confirm-content'
      }
    });
    
    // Retorna true si se hizo clic en "Aceptar", false en cualquier otro caso
    // Esto mantiene la compatibilidad exacta con confirm() nativo
    return result.isConfirmed;
  }
} 